/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';
import { AvatarImageComponent } from '@ng-falcon/features/avatar/avatar-image/avatar-image.component';
import { AvatarModule } from '@ng-falcon/features/avatar/avatar.module';
import { DropdownComponent } from '@ng-falcon/dropdown/dropdown.component';
import { DropdownModule } from '@ng-falcon/dropdown/dropdown.module';
import {
  TESTER_NAVIGATION_HEADER_SERVICE,
  NavHeaderUser,
  NavHeaderAccountUrls,
  TesterNavigationHeaderService,
} from '../tester-navigation-header.service';

const COPY_FEEDBACK_DURATION_MS = 2000;

@Component({
    selector: 'ngf-tester-navigation-header-account',
    templateUrl: './tester-navigation-header-account.component.html',
    styleUrls: ['./tester-navigation-header-account.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        MatIconModule,
        DropdownModule,
        AvatarModule,
    ]
})
export class TesterNavigationHeaderAccountComponent implements OnInit, OnDestroy {
  @ViewChild('dropdown') dropdown?: DropdownComponent;
  @ViewChildren(AvatarImageComponent) avatarImages?: QueryList<AvatarImageComponent>;

  currentUser: NavHeaderUser | null = null;
  accountUrls: NavHeaderAccountUrls | null = null;
  isTextCopied = false;
  dropdownOpened = false;

  private copyTimeoutId: number | null = null;
  private avatarChangedSubscription?: Subscription;

  constructor(
    @Inject(TESTER_NAVIGATION_HEADER_SERVICE) private navHeaderService: TesterNavigationHeaderService,
    private cdr: ChangeDetectorRef,
  ) {}

  get isTTL(): boolean {
    return this.currentUser?.isTTL === true;
  }

  get isLite(): boolean {
    return this.currentUser?.isLite === true;
  }

  /**
   * Accessible name for the account button. The avatar is button content, so an img
   * `alt` would not be announced (a button's aria-label is its whole accessible name);
   * the avatar's meaning has to live here. When the avatar type is unknown the label
   * stays plain "Account Options".
   */
  get accountButtonLabel(): string {
    if (this.currentUser?.hasCustomAvatar === true) {
      return 'Account Options, Your current avatar';
    }
    if (this.currentUser?.hasCustomAvatar === false) {
      return 'Account Options, Default avatar';
    }
    return 'Account Options';
  }

  ngOnInit(): void {
    this.navHeaderService.getCurrentUser().then((user) => {
      this.currentUser = user;
      this.cdr.markForCheck();
    });

    this.navHeaderService.getAccountUrls().then((urls) => {
      this.accountUrls = urls;
      this.cdr.markForCheck();
    });

    this.avatarChangedSubscription = this.navHeaderService.avatarChanged$.subscribe(() => {
      this.avatarImages?.forEach((img) => img.refresh());
    });
  }

  ngOnDestroy(): void {
    this.clearCopyTimeout();
    this.avatarChangedSubscription?.unsubscribe();
  }

  onMenuLinkClick(): void {
    this.dropdown?.close();
    this.dropdownOpened = false;
    this.cdr.detectChanges();
  }

  onDropdownOpened(): void {
    this.dropdownOpened = this.dropdown?.isOpen ?? false;
  }

  onDropdownClosed(): void {
    this.dropdownOpened = false;
    this.cdr.detectChanges();
  }

  onCopyUserId(): void {
    const userId = this.currentUser?.platformId;
    if (!userId) {
      return;
    }

    this.clearCopyTimeout();

    navigator.clipboard.writeText(String(userId))
      .then(() => {
        this.isTextCopied = true;
        this.cdr.markForCheck();

        this.copyTimeoutId = window.setTimeout(() => {
          this.isTextCopied = false;
          this.copyTimeoutId = null;
          this.cdr.detectChanges();
        }, COPY_FEEDBACK_DURATION_MS);
      });
  }

  onSignOut(): void {
    this.navHeaderService.logout();
  }

  private clearCopyTimeout(): void {
    if (this.copyTimeoutId) {
      clearTimeout(this.copyTimeoutId);
      this.copyTimeoutId = null;
    }
  }
}
